export const APP_NAME = import.meta.env.VITE_APP_NAME || "FinHarv"
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || "1.0.0"

// In development: use relative path /api/v1 to leverage Vite proxy
// In production: use the API URL (same domain via CF Worker proxy, or direct if CORS configured)
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/v1"

// Legacy routes (for backward compatibility redirects)
export const LEGACY_ROUTES = {
    PONDS: "/ponds",
    WATER_QUALITY: "/water-quality",
    FISH_HEALTH: "/fish-health",
    MAINTENANCE: "/maintenance",
    FEED: "/feed",
    AI: "/ai",
} as const

// Static routes (no params)
export const ROUTES = {
    HOME: "/",
    LOGIN: "/login",
    SIGNUP: "/signup",
    FARMS: "/farms",
    PONDS: "/ponds", // Legacy redirect - will redirect to last farm's ponds
    WATER_QUALITY: "/water-quality", // Legacy redirect
    FISH_HEALTH: "/fish-health", // Legacy redirect
    MAINTENANCE: "/maintenance", // Legacy redirect
    FEED: "/feed", // Legacy redirect
    AI: "/ai", // Legacy redirect - will redirect to last farm's AI insights
    TEAM: "/team",
    SETTINGS: "/settings",
} as const

// Route patterns (with params)
export const ROUTE_PATTERNS = {
    FARM: "/farms/:farmId",
    FARM_PONDS: "/farms/:farmId/ponds",
    FARM_POND_DETAIL: "/farms/:farmId/ponds/:pondId",
    FARM_WATER_QUALITY: "/farms/:farmId/water-quality",
    FARM_WATER_QUALITY_TRENDS: "/farms/:farmId/water-quality/trends",
    FARM_WATER_QUALITY_DETAIL: "/farms/:farmId/ponds/:pondId/water-quality/:testId",
    FARM_FISH_HEALTH: "/farms/:farmId/fish-health",
    FARM_FISH_HEALTH_TRENDS: "/farms/:farmId/fish-health/trends",
    FARM_FISH_HEALTH_DETAIL: "/farms/:farmId/fish-health/:checkId",
    FARM_MAINTENANCE: "/farms/:farmId/maintenance",
    FARM_MAINTENANCE_TRENDS: "/farms/:farmId/maintenance/trends",
    FARM_MAINTENANCE_ALERTS: "/farms/:farmId/maintenance/alerts",
    FARM_MAINTENANCE_DETAIL: "/farms/:farmId/maintenance/:checkId",
    FARM_FEED: "/farms/:farmId/feed",
    FARM_FEED_TRENDS: "/farms/:farmId/feed/trends",
    FARM_FEED_ALERTS: "/farms/:farmId/feed/alerts",
    FARM_FEED_DETAIL: "/farms/:farmId/feed/:recordId",
    FARM_AI: "/farms/:farmId/ai",
    FARM_AI_ROOT_CAUSE: "/farms/:farmId/ai/root-cause",
    FARM_AI_DIAGNOSIS: "/farms/:farmId/ai/diagnosis",
    FARM_AI_HISTORY: "/farms/:farmId/ai/history",
    FARM_AI_DETAIL: "/farms/:farmId/ai/:analysisId",
} as const

// Helper function to build URLs safely with query parameters
function buildUrlWithParams(pathname: string, params?: Record<string, string | string[] | null | undefined>): string {
    const url = new URL(pathname, window.location.origin);

    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                if (Array.isArray(value)) {
                    if (value.length > 0) {
                        url.searchParams.set(key, value.join(','));
                    }
                } else {
                    url.searchParams.set(key, value);
                }
            }
        });
    }

    return url.pathname + url.search;
}

// Route builder functions for type-safe URL construction
export const buildRoute = {
    home: () => ROUTES.HOME,
    login: () => ROUTES.LOGIN,
    signup: () => ROUTES.SIGNUP,
    farms: () => ROUTES.FARMS,
    farm: (farmId: string) => buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}`),
    ponds: (farmId: string) => buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/ponds`),
    pond: (farmId: string, pondId: string) => {
        console.log('the route builder has this', pondId)
        return buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/ponds/${encodeURIComponent(pondId)}`);
    },

    // Farm-scoped features with optional pond filtering via query params
    waterQuality: (farmId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/water-quality`, {
            pondId: pondId || undefined,
        }),

    waterQualityTest: (farmId: string, pondId: string, testId: string) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/ponds/${encodeURIComponent(pondId)}/water-quality/${encodeURIComponent(testId)}`),

    waterQualityDetail: (farmId: string, pondId: string, testId: string) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/ponds/${encodeURIComponent(pondId)}/water-quality/${encodeURIComponent(testId)}`),

    waterQualityNew: (farmId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/water-quality/new`, {
            pondId: pondId || undefined,
        }),

    waterQualityEdit: (farmId: string, testId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/water-quality/${encodeURIComponent(testId)}/edit`, {
            pondId: pondId || undefined,
        }),

    waterQualityTrends: (farmId: string, options?: {
        ponds?: string[]
        metrics?: string[]
        view?: string
        startDate?: string
        endDate?: string
    }) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/water-quality/trends`, {
            ponds: options?.ponds,
            metrics: options?.metrics,
            view: options?.view,
            startDate: options?.startDate,
            endDate: options?.endDate,
        }),

    fishHealth: (farmId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/fish-health`, {
            pondId: pondId || undefined,
        }),

    fishHealthDetail: (farmId: string, checkId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/fish-health/${encodeURIComponent(checkId)}`, {
            pondId: pondId || undefined,
        }),

    fishHealthNew: (farmId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/fish-health/new`, {
            pondId: pondId || undefined,
        }),

    fishHealthEdit: (farmId: string, checkId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/fish-health/${encodeURIComponent(checkId)}/edit`, {
            pondId: pondId || undefined,
        }),

    fishHealthTrends: (farmId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/fish-health/trends`, {
            pondId: pondId || undefined,
        }),

    maintenance: (farmId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/maintenance`, {
            pondId: pondId || undefined,
        }),

    maintenanceDetail: (farmId: string, checkId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/maintenance/${encodeURIComponent(checkId)}`, {
            pondId: pondId || undefined,
        }),

    maintenanceNew: (farmId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/maintenance/new`, {
            pondId: pondId || undefined,
        }),

    maintenanceEdit: (farmId: string, checkId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/maintenance/${encodeURIComponent(checkId)}/edit`, {
            pondId: pondId || undefined,
        }),

    maintenanceTrends: (farmId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/maintenance/trends`, {
            pondId: pondId || undefined,
        }),

    maintenanceAlerts: (farmId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/maintenance/alerts`, {
            pondId: pondId || undefined,
        }),

    feed: (farmId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/feed`, {
            pondId: pondId || undefined,
        }),

    feedDetail: (farmId: string, recordId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/feed/${encodeURIComponent(recordId)}`, {
            pondId: pondId || undefined,
        }),

    feedNew: (farmId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/feed/new`, {
            pondId: pondId || undefined,
        }),

    feedEdit: (farmId: string, recordId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/feed/${encodeURIComponent(recordId)}/edit`, {
            pondId: pondId || undefined,
        }),

    feedTrends: (farmId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/feed/trends`, {
            pondId: pondId || undefined,
        }),

    feedAlerts: (farmId: string, pondId?: string | null) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/feed/alerts`, {
            pondId: pondId || undefined,
        }),

    // AI Insights routes
    ai: (farmId: string) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/ai/root-cause`),

    aiRootCause: (farmId: string) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/ai/root-cause`),

    aiDiagnosis: (farmId: string, options?: { healthCheckId?: string; pondId?: string }) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/ai/diagnosis`, {
            healthCheckId: options?.healthCheckId,
            pondId: options?.pondId,
        }),

    aiHistory: (farmId: string) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/ai/history`),

    aiAnalysisDetail: (farmId: string, analysisId: string) =>
        buildUrlWithParams(`/farms/${encodeURIComponent(farmId)}/ai/${encodeURIComponent(analysisId)}`),

    team: () => ROUTES.TEAM,
    settings: () => ROUTES.SETTINGS,
} as const

// LocalStorage key generators - user-scoped to prevent cross-account data leakage
export const STORAGE_KEYS = {
    // User-scoped keys (preferred)
    lastFarmId: (userId: string) => `finharv_last_farm_id_${userId}`,
    lastPondId: (userId: string) => `finharv_last_pond_id_${userId}`,
    // Legacy keys (for backward compat during migration)
    LAST_FARM_ID: "finharv_last_farm_id",
    LAST_POND_ID: "finharv_last_pond_id",
} as const
